import { Navigate, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { MembershipShimmer } from '../components/shimmer';
import {
  useGetMembershipSettingsQuery,
  useListMembershipPlansQuery,
} from '../store/siteContentApi';
import { ROUTES } from '../constants/routes';
import { Arrow } from '../components/home/Hero';
import {
  UI, EASE, wrap, wrapN, hXl, lead, eyebrow, empty, spin, price as priceSx,
  plans as plansGrid, plan as planCard, planPop, planChk, flagYellow,
  btn, btnGhost, btnYellow, hS,
} from '../styles/tl5';

// Build the CTA target based on admin-configured action.
const ctaHandler = (plan, navigate) => () => {
  switch (plan.cta_action) {
    case 'external':
      if (plan.cta_url) window.open(plan.cta_url, '_blank', 'noopener,noreferrer');
      return;
    case 'support':
      navigate(ROUTES.SUPPORT);
      return;
    case 'none':
      return;
    case 'login':
    default:
      navigate(ROUTES.LOGIN);
  }
};

const TickIcon = (
  <Box
    component="svg"
    viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    sx={{ width: 12, height: 12, minWidth: 12, display: 'block' }}
  >
    <path d="M20 6 9 17l-5-5" />
  </Box>
);

const Membership = () => {
  const navigate = useNavigate();
  const { data: settings, isLoading: settingsLoading } = useGetMembershipSettingsQuery();
  const { data: plans = [], isLoading: plansLoading } = useListMembershipPlansQuery();

  // Gate the whole page behind the admin feature flag. While the settings
  // request is in flight, render a loading state (not the full page) so
  // users don't see the plans flash if the feature is off.
  if (settingsLoading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <Box sx={{ ...spin, width: 28, height: 28, borderWidth: 3 }} />
      </Box>
    );
  }
  if (settings && !settings.is_enabled) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return (
    <Box>
      {/* ═══ DARK HERO — the plan cards overlap its lower edge ═══ */}
      <Box
        component="section"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: 'var(--tl-g9)',
          color: '#fff',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-30%',
            right: '-6%',
            width: 520,
            height: 520,
            borderRadius: '50%',
            border: '1px dashed rgba(95,224,198,.28)',
            animation: 'tl-spin 70s linear infinite',
            pointerEvents: 'none',
          },
        }}
      >
        <Box
          sx={{
            ...wrap,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: '16px',
            paddingBlock: 'clamp(48px, 6vw, 80px) clamp(110px, 10vw, 150px)',
          }}
        >
          <Box component="span" sx={{ ...eyebrow, alignSelf: 'center', background: 'rgba(255,255,255,.1)', color: 'var(--tl-teal-l)' }}>
            TrustLab membership
          </Box>

          <Typography component="h1" sx={{ ...hXl, color: '#fff' }}>
            One membership.<br />
            Your health, <Box component="span" sx={{ color: 'var(--tl-teal-l)' }}>all year.</Box>
          </Typography>

          <Typography sx={{ ...lead, color: '#B6CFC8', maxWidth: 620 }}>
            Member pricing on every test and checkup, for you and the family
            members on your plan.
          </Typography>
        </Box>
      </Box>

      {/* ═══ PLANS ═══ */}
      <Box
        component="section"
        sx={{
          marginTop: 'clamp(-110px, -8vw, -70px)',
          position: 'relative',
          paddingBottom: 'clamp(56px, 7vw, 96px)',
        }}
      >
        <Box sx={wrap}>
          {plansLoading && <MembershipShimmer count={3} />}

          {!plansLoading && plans.length === 0 && (
            <Box sx={{ ...wrapN, ...empty, px: 0 }}>
              <Typography sx={{ fontSize: 32, lineHeight: 1 }}>🪪</Typography>
              <Typography component="h2" sx={hS}>No plans available right now</Typography>
              <Typography sx={{ fontFamily: UI, fontSize: 15, color: 'var(--tl-muted)', maxWidth: 380 }}>
                Membership plans are being updated — check back shortly, or talk to us
                and we&rsquo;ll sort something out.
              </Typography>
              <Box component="button" type="button" onClick={() => navigate(ROUTES.SUPPORT)} sx={{ ...btn, mt: '4px' }}>
                Talk to us
                <Arrow size={16} />
              </Box>
            </Box>
          )}

          {!plansLoading && plans.length > 0 && (
            <Box sx={plansGrid}>
              {plans.map((p, i) => {
                const featured = !!p.featured;
                const isCustom = p.price == null;
                return (
                  <Box
                    key={p.tier}
                    sx={{
                      ...(featured ? planPop : planCard),
                      animation: `tl-stgin .55s ${EASE} both`,
                      animationDelay: `${i * 70}ms`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                      <Typography component="h2" sx={{ ...hS, color: featured ? '#fff' : 'var(--tl-g9)' }}>
                        {p.title || p.tier}
                      </Typography>
                      {featured && <Box component="span" sx={flagYellow}>Most popular</Box>}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap', mt: '14px' }}>
                      <Box component="span" sx={{ ...priceSx, fontSize: 44, color: featured ? '#fff' : 'var(--tl-g9)' }}>
                        {isCustom ? 'Custom' : `${p.price_prefix || '₹'}${Number(p.price).toLocaleString('en-IN')}`}
                      </Box>
                      {!isCustom && (
                        <Box component="span" sx={{ fontFamily: UI, fontSize: 15, color: featured ? '#B6CFC8' : 'var(--tl-muted)' }}>
                          / year
                        </Box>
                      )}
                    </Box>

                    {p.tagline && (
                      <Typography sx={{ fontFamily: UI, fontSize: 14.5, color: featured ? '#B6CFC8' : 'var(--tl-muted)', mt: '4px' }}>
                        {p.tagline}
                      </Typography>
                    )}

                    <Box sx={{ height: '1px', background: featured ? 'rgba(255,255,255,.14)' : 'var(--tl-line)', my: '22px' }} />

                    {(p.perks || []).length > 0 && (
                      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {p.perks.map((perk) => (
                          <Box
                            component="li"
                            key={perk}
                            sx={{
                              display: 'flex', alignItems: 'flex-start', gap: '12px',
                              fontFamily: UI, fontSize: 15, lineHeight: 1.5,
                              color: featured ? '#E4F1ED' : 'var(--tl-g9)',
                            }}
                          >
                            <Box sx={planChk}>{TickIcon}</Box>
                            {perk}
                          </Box>
                        ))}
                      </Box>
                    )}

                    {p.cta_action !== 'none' && (
                      /* The wrapper carries `margin-top: auto` AND a minimum
                         gap. On the button itself the two fight: in the
                         tallest card there is no slack for `auto`, so the
                         button sat flush against the last perk. */
                      <Box sx={{ mt: 'auto', pt: '26px' }}>
                        <Box
                          component="button"
                          type="button"
                          onClick={ctaHandler(p, navigate)}
                          sx={{ ...(featured ? btnYellow : btnGhost), width: '100%' }}
                        >
                          {p.cta_label || 'Join Now'}
                          <Arrow size={16} />
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Membership;
